import { apiClient } from './client.js';
import type {
  CreateOwnerTenantInput,
  CreateAdminCustomerInput,
  CreateManualCreditInput,
  CustomerMembership,
  UpdateAdminCustomerInput,
  AccountCreditTransaction,
  AccountCreditTransactionType,
  AdminReferral,
  CreateMembershipPlanInput,
  GrantManualMembershipInput,
  MembershipPlan,
  NotificationTemplate,
  NotificationType,
  ReferralSettings,
  Tenant,
  TenantSettings,
  TenantUser,
  UpdateNotificationTemplateInput,
  UpdateOwnerTenantInput,
  UpdateReferralSettingsInput,
  UpdateMembershipPlanInput,
  CreatePromoCodeInput,
  PromoCode,
  PromoDerivedStatus,
  PromoScope,
  MarketingCampaign,
  MarketingCampaignReport,
  MarketingDeliveryRow,
  MarketingAudienceFilter,
  CreateMarketingCampaignInput,
  UpdateMarketingCampaignInput,
} from '@atomfit/shared';
import type { PaginatedResult } from '../types/index.js';

export interface FranchiseTenant extends Tenant {
  settings: TenantSettings | null;
  _count: { gyms: number; memberships: number };
}

export interface DashboardTrendPoint {
  date: string;
  label: string;
  revenue: number;
  reservations: number;
}

export interface PlatformDashboard {
  totalTenants: number;
  totalActiveGyms: number;
  totalBookings: number;
  totalRevenue: number;
  days: number;
  trend: DashboardTrendPoint[];
  metrics: {
    created: number;
    cancelled: number;
    attended: number;
    noShow: number;
    registrations: number;
  };
}

export interface FranchiseDashboard {
  totalGyms: number;
  totalBookings: number;
  totalRevenue: number;
  days: number;
  trend: DashboardTrendPoint[];
  metrics: {
    created: number;
    cancelled: number;
    attended: number;
    noShow: number;
    registrations: number;
  };
}

export type DashboardResponse = PlatformDashboard | FranchiseDashboard;

export interface PlatformMembershipPlan extends MembershipPlan {
  _count: { customerMemberships: number; purchases: number };
  franchiseCount?: number;
  totalActiveMemberships?: number;
  totalPurchases?: number;
}

export const adminApi = {
  exportCustomers: (
    params: {
      q?: string;
      registeredFrom?: string;
      registeredTo?: string;
      tenantSlug?: string;
    } = {},
  ) =>
    apiClient.get<Blob>('/admin/customers/export.csv', {
      params,
      responseType: 'blob',
    }),

  listEmailCampaigns: (page = 1) =>
    apiClient.get<PaginatedResult<MarketingCampaign>>('/admin/email-campaigns', {
      params: { page },
    }),

  getEmailCampaign: (campaignId: string) =>
    apiClient.get<MarketingCampaign>(`/admin/email-campaigns/${campaignId}`),

  createEmailCampaign: (data: CreateMarketingCampaignInput) =>
    apiClient.post<MarketingCampaign>('/admin/email-campaigns', data),

  updateEmailCampaign: (campaignId: string, data: UpdateMarketingCampaignInput) =>
    apiClient.put<MarketingCampaign>(`/admin/email-campaigns/${campaignId}`, data),

  duplicateEmailCampaign: (campaignId: string) =>
    apiClient.post<MarketingCampaign>(`/admin/email-campaigns/${campaignId}/duplicate`),

  previewEmailAudience: (audienceFilter: MarketingAudienceFilter) =>
    apiClient.post<{
      count: number;
      sample: Array<{
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
      }>;
    }>('/admin/email-campaigns/audience-preview', { audienceFilter }),

  testEmailCampaign: (campaignId: string) =>
    apiClient.post<{ sentTo: string }>(`/admin/email-campaigns/${campaignId}/test`),

  scheduleEmailCampaign: (campaignId: string, scheduledAt: string) =>
    apiClient.post<MarketingCampaign>(`/admin/email-campaigns/${campaignId}/schedule`, {
      scheduledAt,
    }),

  pauseEmailCampaign: (campaignId: string) =>
    apiClient.post<MarketingCampaign>(`/admin/email-campaigns/${campaignId}/pause`),

  resumeEmailCampaign: (campaignId: string) =>
    apiClient.post<MarketingCampaign>(`/admin/email-campaigns/${campaignId}/resume`),

  cancelEmailCampaign: (campaignId: string) =>
    apiClient.post<MarketingCampaign>(`/admin/email-campaigns/${campaignId}/cancel`),

  getEmailCampaignReport: (campaignId: string) =>
    apiClient.get<MarketingCampaignReport>(`/admin/email-campaigns/${campaignId}/report`),

  listEmailCampaignDeliveries: (campaignId: string, page = 1) =>
    apiClient.get<PaginatedResult<MarketingDeliveryRow>>(
      `/admin/email-campaigns/${campaignId}/deliveries`,
      { params: { page } },
    ),

  listTenants: (page = 1, limit = 20) =>
    apiClient.get<PaginatedResult<FranchiseTenant>>('/admin/tenants', { params: { page, limit } }),

  createTenant: (data: CreateOwnerTenantInput) =>
    apiClient.post<FranchiseTenant>('/admin/tenants', data),

  updateTenant: (tenantId: string, data: UpdateOwnerTenantInput) =>
    apiClient.put<FranchiseTenant>(`/admin/tenants/${tenantId}`, data),

  getStats: () =>
    apiClient.get<{
      totalTenants: number;
      totalActiveGyms: number;
      totalBookings: number;
      totalRevenue: number;
    }>('/admin/stats'),

  getDashboard: (range: { from: string; to: string }, tenantSlug?: string) =>
    apiClient.get<DashboardResponse>('/admin/dashboard', {
      params: { ...range, ...(tenantSlug ? { tenantSlug } : {}) },
    }),

  listNotificationTemplates: () =>
    apiClient.get<{ data: NotificationTemplate[] }>('/admin/notification-templates'),

  updateNotificationTemplate: (type: NotificationType, data: UpdateNotificationTemplateInput) =>
    apiClient.put<NotificationTemplate>(`/admin/notification-templates/${type}`, data),

  listCredits: (
    params: {
      accountId?: string;
      type?: AccountCreditTransactionType;
      q?: string;
      page?: number;
    } = {},
  ) => apiClient.get<PaginatedResult<AccountCreditTransaction>>('/admin/credits', { params }),

  createManualCredit: (data: CreateManualCreditInput) =>
    apiClient.post<AccountCreditTransaction>('/admin/credits/manual', data),

  createCustomer: (data: CreateAdminCustomerInput) =>
    apiClient.post<TenantUser>('/admin/customers', data),

  updateCustomer: (accountId: string, data: UpdateAdminCustomerInput) =>
    apiClient.put<TenantUser>(`/admin/customers/${accountId}`, data),

  deleteCustomer: (accountId: string) => apiClient.delete(`/admin/customers/${accountId}`),

  grantManualMembership: (accountId: string, data: GrantManualMembershipInput) =>
    apiClient.post(`/admin/customers/${accountId}/membership`, data),

  cancelMembership: (accountId: string, membershipId: string) =>
    apiClient.post<CustomerMembership>(
      `/admin/customers/${accountId}/memberships/${membershipId}/cancel`,
    ),

  listReferrals: (
    params: {
      status?: 'PENDING' | 'ACTIVATED' | 'PAID_OUT';
      q?: string;
      page?: number;
    } = {},
  ) =>
    apiClient.get<{
      settings: ReferralSettings;
      referrals: PaginatedResult<AdminReferral>;
      counts: { pending: number; activated: number; paidOut: number };
    }>('/admin/referrals', { params }),

  getReferralSettings: () => apiClient.get<ReferralSettings>('/admin/referrals/settings'),

  updateReferralSettings: (data: UpdateReferralSettingsInput) =>
    apiClient.put<ReferralSettings>('/admin/referrals/settings', data),

  listMembershipPlans: () =>
    apiClient.get<{ data: PlatformMembershipPlan[] }>('/admin/membership-plans'),

  createMembershipPlan: (data: CreateMembershipPlanInput) =>
    apiClient.post<MembershipPlan>('/admin/membership-plans', data),

  updateMembershipPlan: (tier: string, data: UpdateMembershipPlanInput) =>
    apiClient.put<MembershipPlan>(`/admin/membership-plans/${tier}`, data),

  listPromoCodes: (
    params: {
      status?: PromoDerivedStatus;
      scope?: PromoScope;
      tenantId?: string;
      q?: string;
      page?: number;
      limit?: number;
    } = {},
  ) => apiClient.get<PaginatedResult<PromoCode>>('/admin/promo-codes', { params }),

  createPromoCode: (data: CreatePromoCodeInput) =>
    apiClient.post<PromoCode>('/admin/promo-codes', data),

  updatePromoCodeStatus: (promoCodeId: string, isActive: boolean) =>
    apiClient.patch<PromoCode>(`/admin/promo-codes/${promoCodeId}/status`, { isActive }),
};

export const ownerApi = {
  getStats: () =>
    apiClient.get<{
      totalGyms: number;
      totalBookings: number;
      totalRevenue: number;
    }>('/owner/stats'),

  getDashboard: (range: { from: string; to: string }) =>
    apiClient.get<FranchiseDashboard>('/owner/dashboard', { params: range }),

  listBookings: (page = 1) => apiClient.get('/owner/bookings', { params: { page } }),
};
